import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaTimes } from 'react-icons/fa';
import { googleLoginHref } from './xp';
import GoogleSignInButton from './GoogleSignInButton';

export const VIDEO_SUBMITTED_EVENT = 'bigplays:video-submitted';

const VideoSubmissionModal = ({ isOpen, onClose, isAuthenticated, appUser, hallSlug = 'commons' }) => {
    const [step, setStep] = useState(1); // 1 = terms, 2 = form
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [isFeatured, setIsFeatured] = useState(false);
    const [isLive, setIsLive] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const modalContentRef = useRef(null);

    const slug = String(hallSlug || 'commons');
    const isUserHall = slug.startsWith('@');
    const isHallOwner = !!(appUser && (
      appUser.status === 'Owner' ||
      (isUserHall && `@${appUser.username}`.toLowerCase() === slug.toLowerCase())
    ));
    const canFeature = isUserHall
      ? isHallOwner
      : !!(appUser && ['Moderator', 'Admin', 'Owner'].includes(appUser.status));
    const canMarkLive = isUserHall
      ? isHallOwner
      : !!(appUser && ['Admin', 'Owner'].includes(appUser.status));
    const hallLabel = isUserHall || slug.toLowerCase() !== 'commons' ? slug : 'Commons';

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setYoutubeUrl('');
            setIsFeatured(false);
            setIsLive(false);
            setError('');
            setSuccess(false);
        }
    }, [isOpen]);

    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && modalContentRef.current && !modalContentRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleAgree = () => {
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        if (!youtubeUrl.trim()) {
            setError('Please enter a YouTube URL');
            setIsSubmitting(false);
            return;
        }

        try {
            // Use the current domain for API requests to maintain session cookies
            const apiUrl = '/api/videos/submit';

            console.log('Submitting video to:', apiUrl);

            const response = await axios.post(apiUrl, { 
                youtubeUrl,
                isFeatured: canFeature && isFeatured,
                isLive: canMarkLive && isLive,
                hallSlug: slug
            }, {
                withCredentials: true
            });

            console.log('Submission response:', response.data);

            window.dispatchEvent(new CustomEvent(VIDEO_SUBMITTED_EVENT, { detail: { hallSlug: slug } }));
            setSuccess(true);
            setYoutubeUrl('');
            setTimeout(() => {
                onClose();
                setSuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Error submitting video:', error);
            setError(error.response?.data?.error || 'Failed to submit video. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div ref={modalContentRef} className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white"
                    aria-label="Close"
                >
                    <FaTimes size={20} />
                </button>

                {!isAuthenticated ? (
                    <div className="text-center py-8">
                        <h2 className="text-xl font-bold mb-4">Authentication Required</h2>
                        <p className="mb-6">You need to be logged in to submit videos.</p>

                        <GoogleSignInButton href={googleLoginHref()} className="max-w-xs mx-auto" />
                    </div>
                ) : step === 1 ? (
                    <div>
                        <h2 className="text-xl font-bold mb-4 text-center">Submission Guidelines</h2>
                        <div className="bg-gray-700 p-4 mb-4 rounded-lg text-gray-200">
                            <p className="mb-4">BIGPLAYS.GG is intended to showcase short (45 seconds or less) clips of Counter-Strike 2 highlights only. Videos must be in the form of Youtube links. NO YOUTUBE SHORTS.</p>
                            <p className="mb-4">Submissions in violation of this will be deleted and user accounts will be terminated without warning.</p>
                            <p className="mb-4 text-xs">*We reserve the right to remove any submission at any time and/or ban any user for any reason. By submitting a video to BIGPLAYS.GG, you grant us the right to promote, share, and distribute the video across any platform or medium, including but not limited to social media, partner sites, and promotional materials.</p>
                        </div>
                        <div className="flex justify-between">
                            <button
                                onClick={onClose}
                                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                            >
                                Nevermind
                            </button>
                            <button
                                onClick={handleAgree}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                            >
                                Agree
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h2 className="text-xl font-bold mb-4 text-center">Submit a Video</h2>
                        {success ? (
                            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                                Video submitted to {hallLabel}!
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {error && (
                                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                                        {error}
                                    </div>
                                )}
                                <p className="text-gray-400 text-sm mb-4">
                                    This clip will be added to <span className="text-white font-semibold">{hallLabel}</span> only.
                                </p>
                                <div className="mb-4">
                                    <label htmlFor="youtubeUrl" className="block text-gray-300 mb-2">
                                        Paste Youtube link here:
                                    </label>
                                    <input
                                        type="text"
                                        id="youtubeUrl"
                                        value={youtubeUrl}
                                        onChange={(e) => setYoutubeUrl(e.target.value)}
                                        placeholder="Example: https://www.youtube.com/watch?v=qIHmRN93Oj0"
                                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                                        disabled={isSubmitting}
                                    />
                                </div>
                                {canFeature && (
                                    <div className="mb-4">
                                        <label className="flex items-center text-gray-300 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isFeatured}
                                                onChange={(e) => setIsFeatured(e.target.checked)}
                                                className="mr-2 w-4 h-4"
                                                disabled={isSubmitting}
                                            />
                                            <span>Featured — play this on the {hallLabel} TV</span>
                                        </label>
                                    </div>
                                )}
                                {canMarkLive && (
                                    <div className="mb-4">
                                        <label className="flex items-center text-gray-300 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isLive}
                                                onChange={(e) => setIsLive(e.target.checked)}
                                                className="mr-2 w-4 h-4"
                                                disabled={isSubmitting}
                                            />
                                            <span>Live — add this stream to the {hallLabel} live feed</span>
                                        </label>
                                        <p className="text-gray-400 text-xs mt-1 ml-6">
                                            Must be a currently live YouTube stream. It will leave the live section when the broadcast ends.
                                        </p>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setStep(1)}
                                        className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                                        disabled={isSubmitting}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Submitting...
                                            </>
                                        ) : (
                                            'Submit'
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoSubmissionModal; 